import React, { useState } from "react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LockKey, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";

export default function ForcePasswordChange() {
    const { user, setUser } = useAuth();
    const [form, setForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
    const [saving, setSaving] = useState(false);

    async function submit(e) {
        e.preventDefault();
        if (form.new_password !== form.confirm_password) {
            toast.error("New passwords do not match");
            return;
        }
        if (form.new_password.length < 8) {
            toast.error("New password must be at least 8 characters long");
            return;
        }
        setSaving(true);
        try {
            await api.post("/users/me/change-password", {
                current_password: form.current_password,
                new_password: form.new_password,
            });
            toast.success("Password updated — welcome to maintayn.");
            setUser({ ...user, must_change_password: false });
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="min-h-screen bg-brand-ink flex items-center justify-center px-4" data-testid="force-password-change-screen">
            <div className="w-full max-w-md bg-white rounded-lg shadow-2xl p-8">
                <div className="flex items-center gap-2 mb-6">
                    <Logo size={30} variant="onDark" />
                </div>
                <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-brand-sage flex items-center justify-center shrink-0">
                        <ShieldCheck size={20} className="text-brand-action" weight="fill" />
                    </div>
                    <h1 className="font-heading font-bold text-xl text-brand-ink">Set a new password</h1>
                </div>
                <p className="text-sm text-brand-inkSoft mb-6">
                    For security, please set a new password before continuing to your account.
                </p>
                <form onSubmit={submit} className="space-y-4">
                    <div className="space-y-2">
                        <Label>Current (temporary) password</Label>
                        <Input
                            data-testid="force-password-current"
                            type="password"
                            required
                            value={form.current_password}
                            onChange={(e) => setForm({ ...form, current_password: e.target.value })}
                            className="rounded-sm border-brand-line"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label>New password</Label>
                        <Input
                            data-testid="force-password-new"
                            type="password"
                            required
                            minLength={8}
                            value={form.new_password}
                            onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                            placeholder="Min 8 characters"
                            className="rounded-sm border-brand-line"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label>Confirm new password</Label>
                        <Input
                            data-testid="force-password-confirm"
                            type="password"
                            required
                            minLength={8}
                            value={form.confirm_password}
                            onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
                            className="rounded-sm border-brand-line"
                        />
                    </div>
                    <Button
                        type="submit"
                        data-testid="force-password-submit"
                        disabled={saving}
                        className="w-full rounded-full bg-brand-action hover:bg-brand-actionHover text-white mt-2"
                    >
                        <LockKey size={16} className="mr-1.5" />
                        {saving ? "Updating…" : "Set new password & continue"}
                    </Button>
                </form>
            </div>
        </div>
    );
}
